import { api } from './client'

/**
 * F-102 (REQ-068-001, REQ-068-002): stati configurabili del lead, sopra il ciclo di vita fisso
 * (`new | contacted | qualified | converted | lost`). `api/lead-statuses`: lettura per ogni utente
 * autenticato, scrittura solo Admin.
 */
export type LeadStatusKind = 'operativo' | 'esito_finale'

export type LeadLifecycleStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost'

export interface LeadStatusDefinition {
  id: number
  code: string
  label: string
  kind: LeadStatusKind
  lifecycleStatus: LeadLifecycleStatus
  /** Solo per un esito finale Perso: sta nel vocabolario dei motivi di perdita. */
  lostReason: string | null
  sortOrder: number
  isActive: boolean
  isStandard: boolean
  /** Quanti lead portano questo stato (anche nel cestino). */
  leads: number
}

/** Una voce della struttura standard (24 stati della specifica); `derivato` = calcolato, non si scrive. */
export interface LeadStatusStandardEntry {
  code: string
  label: string
  kind: LeadStatusKind | 'derivato'
  lifecycleStatus: LeadLifecycleStatus | null
  lostReason: string | null
}

export interface LeadStatusList {
  kinds: LeadStatusKind[]
  lifecycleStatuses: Record<LeadStatusKind, LeadLifecycleStatus[]>
  lostReasons: string[]
  standard: LeadStatusStandardEntry[]
  items: LeadStatusDefinition[]
}

export interface LeadStatusDefinitionBody {
  /** Solo in creazione: in modifica, se c'e', deve essere lo stesso. */
  code?: string
  label: string
  kind: LeadStatusKind
  lifecycleStatus: LeadLifecycleStatus
  lostReason?: string | null
  sortOrder?: number
  isActive?: boolean
}

export interface ApplyStandardResult {
  inserted: number
  alreadyPresent: number
  derived: number
  codes: string[]
}

export const LIFECYCLE_LABELS: Record<LeadLifecycleStatus, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  converted: 'Convertito',
  lost: 'Perso',
}

export const KIND_LABELS: Record<LeadStatusKind | 'derivato', string> = {
  operativo: 'Stato operativo',
  esito_finale: 'Esito finale',
  derivato: 'Calcolato',
}

export const leadStatusesApi = {
  list: (activeOnly = false) =>
    api<LeadStatusList>(`/api/lead-statuses${activeOnly ? '?activeOnly=true' : ''}`),
  create: (body: LeadStatusDefinitionBody) =>
    api<LeadStatusDefinition>('/api/lead-statuses', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: LeadStatusDefinitionBody) =>
    api<LeadStatusDefinition>(`/api/lead-statuses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: number) => api<void>(`/api/lead-statuses/${id}`, { method: 'DELETE' }),
  /** Inserisce gli stati standard mancanti (idempotente). Solo su azione esplicita dell'Admin. */
  applyStandard: () =>
    api<ApplyStandardResult>('/api/lead-statuses/apply-standard', { method: 'POST' }),
}
